import { Link, useSearchParams } from 'react-router-dom';
import { Search, Plus, Users, Megaphone, Mail, MessageSquare, ArrowUpRight, Workflow, ThumbsUp, ThumbsDown, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { get } from '../../lib/apiClient';
import { API_URL } from '../../_config';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { type InboxThread, useInboxCountsQuery, useInboxQuery } from '../../hooks/useInbox';

interface DashboardStats {
  total_leads: number;
  leads_this_month: number;
  active_campaigns: number;
  campaigns_created_today: number;
  emails_sent: number;
  emails_sent_this_month: number;
}

interface LeadGrowthPoint {
  date: string;
  count: number;
}

interface CampaignPerformanceItem {
  name: string;
  sent: number;
  reply_pct: number;
  open_pct: number;
}

interface DashboardAnalytics {
  top_campaigns: CampaignPerformanceItem[];
}

function formatTimeAgo(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

interface BillingData {
  plan: string;
  plan_status: string;
  trial_ends_at: string | null;
  credits: { lead: number; email: number };
}

interface CreditsBalance {
  lead_credits_remaining: number;
  lead_credits_total: number;
  email_credits_remaining: number;
  email_credits_total: number;
  is_unlimited: boolean;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('payment') === 'success';

  const { data: billingData } = useQuery({
    queryKey: ['billing'],
    queryFn: () => get<BillingData>('billing'),
  });

  const { data: creditsData } = useQuery({
    queryKey: ['credits-balance'],
    queryFn: () => get<CreditsBalance>('credits/balance'),
  });

  const { data: statsData } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => get<DashboardStats>(API_URL.ANALYTICS.OVERVIEW),
    staleTime: 60_000,
  });
  const stats = statsData?.data;

  const { data: leadGrowthData } = useQuery({
    queryKey: ['lead-growth'],
    queryFn: () => get<{ series: LeadGrowthPoint[] }>(API_URL.ANALYTICS.LEAD_GROWTH),
    staleTime: 60_000,
  });

  const { data: dashboardData } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => get<DashboardAnalytics>(`${API_URL.ANALYTICS.DASHBOARD}?period=30d`),
    staleTime: 60_000,
  });

  const leadSeries = leadGrowthData?.data?.series ?? [];
  const topCampaigns = dashboardData?.data?.top_campaigns ?? [];

  const { data: inboxCounts } = useInboxCountsQuery({});

  const { data: inboxRecentData } = useInboxQuery({
    per_page: 5,
    page: 1,
    sort: 'last_message_at',
    order: 'desc',
    status: 'replied',
  });
  const recentInboxReplies = (inboxRecentData?.data?.messages ?? []) as InboxThread[];

  const { items: recentlyViewedItems } = useRecentlyViewed();
  const recentLeads = recentlyViewedItems.slice(0, 10);

  const billing = billingData?.data;
  const credits = creditsData?.data;
  const isTrialing = billing?.plan_status === 'trialing';

  const hasAdvancedAnalytics = !!(user?.organization?.plan_features?.advanced_analytics);
  const isActiveOrTrial = user?.planStatus === 'active' || user?.planStatus === 'trialing';
  const showFullDashboard = !!(hasAdvancedAnalytics && isActiveOrTrial);
  const canNavigateKpi = hasAdvancedAnalytics || user?.planStatus !== 'active';
  const trialEndsAt = billing?.trial_ends_at ? new Date(billing.trial_ends_at) : null;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / 86400000)) : null;

  const repliesCount = inboxCounts?.replied;
  const replyRate =
    typeof repliesCount === 'number' && typeof stats?.emails_sent === 'number' && stats.emails_sent > 0
      ? (repliesCount / stats.emails_sent) * 100
      : null;

  const kpis = [
    {
      label: 'Total Leads',
      value: stats ? stats.total_leads.toLocaleString() : '—',
      sub: stats ? `+${stats.leads_this_month.toLocaleString()} this month` : '...',
      color: 'var(--sky)',
      icon: Users,
      href: '/app/leads',
    },
    {
      label: 'Active Campaigns',
      value: stats ? stats.active_campaigns.toLocaleString() : '—',
      sub: stats ? `${stats.campaigns_created_today} Created Today` : '...',
      color: 'var(--green)',
      icon: Megaphone,
      href: '/app/campaigns',
    },
    {
      label: 'Emails Sent',
      value: stats ? stats.emails_sent.toLocaleString() : '—',
      sub: stats ? `+${stats.emails_sent_this_month.toLocaleString()} this month` : '...',
      color: 'var(--amber)',
      icon: Mail,
      href: '/app/replies',
    },
    {
      label: 'Need Reply',
      value: typeof repliesCount === 'number' ? repliesCount.toLocaleString() : '—',
      sub: replyRate === null ? '...' : `${replyRate.toFixed(1)}% reply rate`,
      color: 'var(--navy)',
      icon: MessageSquare,
      href: '/app/replies',
    },
  ];

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="portal-page db-page">
      {paymentSuccess && (
        <div className="db-banner db-banner-success">
          Payment successful! Your plan is now active.
        </div>
      )}
      {isTrialing && trialDaysLeft !== null && (
        <div className="db-banner db-banner-trial">
          You're on a free trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining.
          <Link to="/app/upgrade-plan" className="db-banner-link">Upgrade now</Link>
        </div>
      )}
      <div className="db-header">
        <div>
          <h1 className="db-greeting">{greeting}, {user?.firstName || 'there'}!</h1>
          <p className="db-date">{dateStr}</p>
        </div>
        <div className="db-actions">
          <Link to="/app/leads" className="btn btn-secondary btn-sm"><Search size={16} /> Search Leads</Link>
          <Link to="/app/workflows/new" className="btn btn-primary btn-sm"><Workflow size={16} /> Build Workflow</Link>
          {showFullDashboard && (
            <>
              <Link to="/app/campaigns/new" className="btn btn-secondary btn-sm"><Plus size={16} /> New Campaign</Link>
              <Link to="/app/replies" className="btn btn-secondary btn-sm db-replies-btn">
                <MessageSquare size={16} /> Need Reply
                {typeof repliesCount === 'number' && repliesCount > 0 && (
                  <span className="db-badge-alert">{repliesCount}</span>
                )}
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="db-kpi-grid">
        {kpis.map((kpi) => {
          const inner = (
            <div className="db-kpi-inner">
              <div>
                <p className="db-kpi-label">{kpi.label}</p>
                <p className="db-kpi-value">{kpi.value}</p>
                <p className="db-kpi-sub">
                  <ArrowUpRight size={14} /> {kpi.sub}
                </p>
              </div>
              <div className="db-kpi-icon" style={{ background: `${kpi.color}15` }}>
                <kpi.icon size={22} color={kpi.color} />
              </div>
            </div>
          );
          return canNavigateKpi ? (
            <Link
              key={kpi.label}
              to={kpi.href}
              className="card db-kpi-card"
              style={{ borderLeft: `4px solid ${kpi.color}`, textDecoration: 'none', display: 'block' }}
            >
              {inner}
            </Link>
          ) : (
            <div
              key={kpi.label}
              className="card db-kpi-card"
              style={{ borderLeft: `4px solid ${kpi.color}`, cursor: 'default' }}
            >
              {inner}
            </div>
          );
        })}
      </div>

      {!showFullDashboard && (
        <div className="card" style={{ textAlign: 'center', padding: '32px 24px', marginBottom: 32 }}>
          <p style={{ fontSize: 15, color: 'var(--navy)', fontWeight: 600, marginBottom: 8 }}>
            Full Dashboard requires a Gold or Platinum plan
          </p>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20 }}>
            Upgrade to unlock performance charts, recent leads, recent replies, and credit usage insights.
          </p>
          <Link to="/app/upgrade-plan" className="btn btn-primary btn-sm">View Plans</Link>
        </div>
      )}

      {showFullDashboard && (
        <>
        <div className="db-charts-grid">
        <div className="card">
          <h3 className="db-section-title">Lead Growth</h3>
          <div className="db-chart-area">
            {leadSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={leadSeries} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="lgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--sky)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="var(--sky)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [v, 'Leads']} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="var(--sky)"
                    strokeWidth={2.5}
                    fill="url(#lgGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="db-chart-empty">No lead data yet</div>
            )}
          </div>
        </div>
        <div className="card">
          <h3 className="db-section-title">Campaign Performance</h3>
          <div className="db-bar-chart">
            {topCampaigns.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={topCampaigns} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: string) => v.length > 8 ? v.slice(0, 8) + '…' : v}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v, name) => [v, name === 'sent' ? 'Emails Sent' : String(name)]}
                  />
                  <Bar dataKey="sent" radius={[3, 3, 0, 0]}>
                    {topCampaigns.map((_, i) => (
                      <Cell key={i} fill={i % 2 === 0 ? 'var(--sky)' : '#7dd3fc'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="db-chart-empty">No campaign data yet</div>
            )}
          </div>
        </div>
      </div>

      <div className="db-lists-grid">
        <div className="card">
          <div className="db-list-header">
            <h3 className="db-section-title">Recent Leads</h3>
            <Link to="/app/leads" state={{ returnTab: 'Recently Viewed' }} className="db-view-all">View all</Link>
          </div>
          <div className="db-table-wrap">
            <table className="db-table">
              <thead>
                <tr className="db-table-head-row">
                  <th className="db-th">Name</th>
                  <th className="db-th">Company</th>
                  <th className="db-th">Title</th>
                  <th className="db-th db-th-right">Added</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id} className="db-table-row">
                    <td className="db-td">
                      <div className="db-lead-name">
                        <div className="db-avatar">
                          {lead.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {lead.name}
                      </div>
                    </td>
                    <td className="db-td db-td-muted">{lead.company}</td>
                    <td className="db-td db-td-muted">{lead.title}</td>
                    <td className="db-td db-td-muted db-td-right db-td-small">{lead.added}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {recentLeads.length === 0 && (
              <div className="db-empty-state">
                <p className="db-empty-title">No recent leads</p>
                <p className="db-empty-subtitle">Leads you view or add will appear here.</p>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="db-list-header">
            <h3 className="db-section-title">Recent Replies</h3>
            <Link to="/app/replies?tab=replied" className="db-view-all">View all</Link>
          </div>
          <div className="db-replies-list">
            {recentInboxReplies.map((r) => {
              const name = r.contact_name ?? r.contact_email ?? 'Unknown';
              const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              const campaign = r.campaign?.name ?? r.campaign_name ?? r.workflow_name ?? r.sequence_name ?? '—';
              const snippet = r.last_message_preview ?? '';
              const time = r.last_message_at ? formatTimeAgo(r.last_message_at) : '';
              const sentimentTag = (() => {
                if (r.sentiment === 'positive')   return { label: 'Positive',   color: '#4CAF50', bg: '#E8F5E9', Icon: ThumbsUp };
                if (r.sentiment === 'interested') return { label: 'Interested', color: '#F5A623', bg: '#FFF8E1', Icon: Star };
                if (r.sentiment === 'negative')   return { label: 'Negative',   color: '#E53935', bg: '#FFEBEE', Icon: ThumbsDown };
                return null;
              })();
              const statusBadge = (() => {
                if (r.reply_status === 'replied') return { label: 'Replied',            color: '#4CAF50', bg: '#E8F5E9' };
                if (r.reply_status === 'waiting') return { label: 'Waiting Reply',      color: '#E53935', bg: '#FFEBEE' };
                if (r.reply_status === 'seen')    return { label: 'Seen (Not Replied)', color: '#F5A623', bg: '#FFF8E1' };
                return                                    { label: 'Waiting Reply',     color: '#E53935', bg: '#FFEBEE' };
              })();
              return (
                <Link
                  key={r.id}
                  to={`/app/replies?thread=${encodeURIComponent(r.id)}&scroll=1`}
                  className="db-reply-card"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="db-reply-top">
                    <div className="db-reply-user">
                      <div className="db-avatar-sm">{initials}</div>
                      <span className="db-reply-name">{name}</span>
                    </div>
                    <span className="db-reply-time">{time}</span>
                  </div>
                  <p className="db-reply-campaign">{campaign}</p>
                  <p className="db-reply-snippet">{snippet}</p>
                  <div className="db-reply-tags">
                    {sentimentTag && (
                      <span className="db-reply-badge" style={{ background: sentimentTag.bg, color: sentimentTag.color }}>
                        <sentimentTag.Icon size={10} /> {sentimentTag.label}
                      </span>
                    )}
                    <span className="db-reply-badge" style={{ background: statusBadge.bg, color: statusBadge.color }}>
                      {statusBadge.label}
                    </span>
                  </div>
                </Link>
              );
            })}
            {recentInboxReplies.length === 0 && (
              <div className="db-empty-state">
                <p className="db-empty-title">No recent replies</p>
                <p className="db-empty-subtitle">New replies will appear here as they arrive.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="db-section-title db-credit-title">Credit Usage</h3>
        <div className="db-credit-grid">
          {(() => {
            const leadUsed = credits ? Math.max(0, credits.lead_credits_total - credits.lead_credits_remaining) : 0;
            const leadPct = credits && credits.lead_credits_total > 0 ? Math.min(100, (leadUsed / credits.lead_credits_total) * 100) : 0;
            const leadOverLimit = credits && !credits.is_unlimited && credits.lead_credits_remaining < 0;
            const emailUsed = credits ? Math.max(0, credits.email_credits_total - credits.email_credits_remaining) : 0;
            const emailPct = credits && credits.email_credits_total > 0 ? Math.min(100, (emailUsed / credits.email_credits_total) * 100) : 0;
            const emailOverLimit = credits && !credits.is_unlimited && credits.email_credits_remaining < 0;
            return (
              <>
                <div>
                  <div className="db-credit-row">
                    <span className="db-credit-label">Lead Credits</span>
                    <span className="db-credit-value">
                      {credits
                        ? credits.is_unlimited
                          ? '∞ Unlimited'
                          : `${leadUsed.toLocaleString()} / ${credits.lead_credits_total.toLocaleString()}`
                        : '— / —'}
                      {leadOverLimit && (
                        <span className="db-over-limit-badge">Over limit</span>
                      )}
                    </span>
                  </div>
                  <div className="db-progress-track">
                    <div className="db-progress-bar" style={{
                      width: credits ? (credits.is_unlimited ? '100%' : `${leadPct}%`) : '0%',
                      background: leadOverLimit ? 'var(--red, #E53935)' : 'var(--sky)',
                    }} />
                  </div>
                </div>
                <div>
                  <div className="db-credit-row">
                    <span className="db-credit-label">Email Credits</span>
                    <span className="db-credit-value">
                      {credits
                        ? credits.is_unlimited
                          ? '∞ Unlimited'
                          : `${emailUsed.toLocaleString()} / ${credits.email_credits_total.toLocaleString()}`
                        : '— / —'}
                      {emailOverLimit && (
                        <span className="db-over-limit-badge">Over limit</span>
                      )}
                    </span>
                  </div>
                  <div className="db-progress-track">
                    <div className="db-progress-bar" style={{
                      width: credits ? (credits.is_unlimited ? '100%' : `${emailPct}%`) : '0%',
                      background: emailOverLimit ? 'var(--red, #E53935)' : 'var(--amber)',
                    }} />
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
